import { ReactNode, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ConclusaoTipo } from "@workflow/types";
import { CheckCircle2, ClipboardList, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TarefaDTO } from "@/types/tarefa.type";
import { FormSchemaDTO, DadosConclusaoDTO } from "@/types/conclusao.type";
import { resolveResourceLink, useNavigationHelpers } from "@/lib/navigation-helpers";
import { useConcluirTarefa } from "@/hooks/tarefas/use-concluir-tarefa";
import { ConcluirFormularioInline } from "./ConcluirFormularioInline";

interface ConclusaoRendererProps {
  tarefa: TarefaDTO;
}

/**
 * Painel de conclusão da tarefa — um caminho por descritor:
 * - NENHUMA: confirmação simples ("Concluir tarefa").
 * - CTA: leva à tela onde a tarefa é de fato realizada (ação legado).
 * - FORMULARIO: formulário inline; ao enviar, conclui com os dados.
 *
 * Self-contained: traz o próprio card com acento (verde para concluir, âmbar
 * para "realizar em outra tela"), então a página não precisa envolvê-lo.
 */
export function ConclusaoRenderer({ tarefa }: Readonly<ConclusaoRendererProps>) {
  const { goTo } = useNavigationHelpers();
  const { concluir, isConcluindo } = useConcluirTarefa();

  // Default seguro: sem descritor, comporta-se como NENHUMA.
  const conclusao = tarefa.conclusao ?? { tipo: ConclusaoTipo.NENHUMA };

  if (conclusao.tipo === ConclusaoTipo.CTA) {
    return (
      <ConclusaoShell
        accent="amber"
        icon={<ExternalLink className="h-5 w-5" />}
        title="Realizar tarefa"
        subtitle="Esta tarefa é concluída em outra tela do sistema."
      >
        <div className="space-y-2">
          <Button
            className="w-full bg-amber-600 text-white hover:bg-amber-700"
            onClick={() => {
              const path = resolveResourceLink(conclusao.link);
              if (path) {
                goTo(path);
              } else {
                toast.error("Não foi possível encontrar a tela desta tarefa.");
              }
            }}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Ir para a tela
          </Button>

          <Button
            variant="outline"
            className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-950/20"
            onClick={() => concluir({ id: tarefa.id })}
            disabled={isConcluindo}
          >
            <CheckCircle2 className="mr-2 h-4 w-4" />
            Concluir tarefa
          </Button>
        </div>
      </ConclusaoShell>
    );
  }

  if (conclusao.tipo === ConclusaoTipo.FORMULARIO) {
    return (
      <ConclusaoFormulario
        schema={conclusao.schema}
        isConcluindo={isConcluindo}
        onConfirm={(dados) => concluir({ id: tarefa.id, dados })}
      />
    );
  }

  return (
    <ConclusaoShell
      accent="emerald"
      icon={<CheckCircle2 className="h-5 w-5" />}
      title="Concluir tarefa"
      subtitle="Terminou o que foi pedido? Marque a tarefa como concluída."
    >
      <Button
        className="w-full bg-emerald-600 text-white hover:bg-emerald-700"
        onClick={() => concluir({ id: tarefa.id })}
        disabled={isConcluindo}
      >
        <CheckCircle2 className="mr-2 h-4 w-4" />
        Concluir tarefa
      </Button>
    </ConclusaoShell>
  );
}

/**
 * Ramo FORMULARIO: o formulário não aparece de cara. Mostra primeiro um botão
 * "Executar tarefa" e só então revela o formulário inline (com animação).
 * Evita poluir a tela com campos antes de o usuário decidir agir. Campos
 * ARQUIVO dentro do formulário abrem seu próprio modal de upload (ver
 * `CampoArquivoUpload`) — o resto dos campos fica aqui, inline, como sempre foi.
 */
function ConclusaoFormulario({
  schema,
  isConcluindo,
  onConfirm,
}: Readonly<{
  schema: FormSchemaDTO;
  isConcluindo: boolean;
  onConfirm: (dados: DadosConclusaoDTO) => void;
}>) {
  const [aberto, setAberto] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  return (
    <ConclusaoShell
      accent="emerald"
      icon={<ClipboardList className="h-5 w-5" />}
      title={schema.titulo ?? "Executar tarefa"}
      subtitle={
        aberto
          ? "Preencha as informações abaixo para concluir."
          : "Clique para preencher o que é necessário e concluir."
      }
    >
      <AnimatePresence mode="wait" initial={false}>
        {aberto ? (
          <motion.div
            key="form"
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
            animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, height: "auto" }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <ConcluirFormularioInline
              schema={schema}
              isConcluindo={isConcluindo}
              onConfirm={onConfirm}
            />
          </motion.div>
        ) : (
          <Button
            key="cta"
            className="w-full bg-emerald-600 text-white hover:bg-emerald-700"
            onClick={() => setAberto(true)}
          >
            <ClipboardList className="mr-2 h-4 w-4" />
            Executar tarefa
          </Button>
        )}
      </AnimatePresence>
    </ConclusaoShell>
  );
}

type Accent = "emerald" | "amber";

const ACCENT_STYLES: Record<Accent, { border: string; header: string; chip: string }> = {
  emerald: {
    border: "border-emerald-200/70 dark:border-emerald-900/40",
    header: "bg-emerald-50/70 dark:bg-emerald-950/20",
    chip: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  },
  amber: {
    border: "border-amber-200/70 dark:border-amber-900/40",
    header: "bg-amber-50/70 dark:bg-amber-950/20",
    chip: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  },
};

/** Casca visual comum do painel de conclusão: card com acento + cabeçalho. */
function ConclusaoShell({
  accent,
  icon,
  title,
  subtitle,
  children,
}: Readonly<{
  accent: Accent;
  icon: ReactNode;
  title: string;
  subtitle: string;
  children: ReactNode;
}>) {
  const styles = ACCENT_STYLES[accent];

  return (
    <Card className={cn("overflow-hidden shadow-sm", styles.border)}>
      <div
        className={cn("flex items-start gap-3 border-b px-4 py-3.5", styles.header, styles.border)}
      >
        <span className={cn("grid size-9 shrink-0 place-items-center rounded-lg", styles.chip)}>
          {icon}
        </span>
        <div className="min-w-0 space-y-0.5">
          <h3 className="text-sm font-semibold leading-tight text-foreground">{title}</h3>
          <p className="text-xs leading-snug text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      <div className="p-4">{children}</div>
    </Card>
  );
}
